// iter603 / iter606 — Branch Printer Health widget for the Dashboard.
//
// Groups every configured printer by branch and surfaces the per-station
// status at a glance. A multi-kitchen branch like "Downtown" with
// Cold Prep + Hot Grill + Bar shows up as
//   Downtown  Cold Prep ✅ · Hot Grill ❌ · Bar ✅
// so the manager spots a downed lane immediately without expanding.
//
// Probe-fan-out: when the user's machine is running the local Print
// Bridge, the manual refresh button pings every printer in parallel
// and broadcasts results to the server so any other manager viewing
// the dashboard sees the same picture without a bridge of their own.

import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Printer, CheckCircle2, AlertTriangle, Loader2, RefreshCw, ChefHat, Receipt as ReceiptIcon, Tag as TagIcon, GlassWater } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { isBridgeAlive, pingPrinter } from '@/lib/printBridge';

const REFRESH_MS = 60 * 1000;

function timeAgo(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'never';
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function roleIcon(t, size = 11) {
  if (t === 'kitchen') return <ChefHat size={size} />;
  if (t === 'bar') return <GlassWater size={size} />;
  if (t === 'label') return <TagIcon size={size} />;
  return <ReceiptIcon size={size} />;
}

// Pill that summarises one printer (one lane / station): icon + name +
// red/green/grey dot. Compact enough to fit ~3 per row inside a tile.
// iter608 — clickable: tapping it pings just THIS printer via the
// local bridge (no fan-out to the other 9), so the manager can chase
// a single flaky lane without re-probing everything.
function StationPill({ printer, onPing, isPinging }) {
  const dot =
    printer.online === true ? 'bg-emerald-500'
    : printer.online === false ? 'bg-rose-500'
    : 'bg-slate-300';
  const label = printer.station || (
    printer.type === 'receipt' ? 'Cashier'
    : printer.type === 'kitchen' ? 'Kitchen'
    : printer.type === 'bar' ? 'Bar'
    : printer.name
  );
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onPing?.(printer); }}
      disabled={!printer.ip_address || isPinging}
      className="inline-flex items-center gap-1 text-[10px] bg-white/70 hover:bg-white rounded-full px-2 py-0.5 border border-white/80 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
      title={`${printer.name} · ${printer.ip_address || 'no IP'} · ${timeAgo(printer.last_checked)}${printer.last_error ? ' · ' + printer.last_error : ''}\nClick to ping just this printer`}
      data-testid={`printer-pill-${printer.id}`}
    >
      {isPinging
        ? <Loader2 size={10} className="animate-spin text-info" />
        : <span className={`w-1.5 h-1.5 rounded-full ${dot}`} aria-hidden />}
      <span className="opacity-70">{roleIcon(printer.type, 10)}</span>
      <span className="font-semibold truncate max-w-[100px]">{label}</span>
    </button>
  );
}

// One branch row inside the expanded list: branch name + summary +
// per-station pills wrapped onto subsequent lines if needed.
function BranchRow({ branchName, items, onPing, pingingIds }) {
  const online = items.filter((p) => p.online === true).length;
  const offline = items.filter((p) => p.online === false).length;
  const summary =
    offline > 0 ? `${offline} down · ${online}/${items.length}`
    : online === items.length ? `all ${items.length} online`
    : `${online}/${items.length} online`;
  const tone =
    offline > 0 ? 'bg-rose-50 border-rose-100'
    : online === items.length ? 'bg-emerald-50 border-emerald-100'
    : 'bg-amber-50 border-amber-100';
  return (
    <div
      className={`rounded-lg border p-2 ${tone}`}
      data-testid={`branch-row-${branchName}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-xs font-semibold truncate">{branchName}</div>
        <div className="text-[10px] text-muted-foreground shrink-0">{summary}</div>
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((p) => (
          <StationPill
            key={p.id}
            printer={p}
            onPing={onPing}
            isPinging={pingingIds?.has(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default function BranchPrinterHealthWidget() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ printers: [], online: 0, total: 0 });
  const [pinging, setPinging] = useState(false);
  const [pingingIds, setPingingIds] = useState(() => new Set());
  const [expanded, setExpanded] = useState(false);

  // iter608 — Ping ONE specific printer (the one whose chip was
  // clicked) and broadcast the result. Same logic as pingAll but
  // narrowed to a single id so the manager can chase a flaky lane
  // without re-probing everything.
  const pingOne = async (printer) => {
    if (!printer || !printer.ip_address) {
      toast.error(`${printer?.name || 'Printer'} has no IP configured`);
      return;
    }
    setPingingIds((prev) => {
      const next = new Set(prev);
      next.add(printer.id);
      return next;
    });
    try {
      const alive = await isBridgeAlive();
      if (!alive) {
        toast.error('Print Bridge not running on this PC — install it first.');
        return;
      }
      const r = await pingPrinter(printer.ip_address, printer.port || 9100);
      const online = !!r.ok;
      const err = r.ok ? null : (r.data?.error || 'unreachable');
      await api
        .post(`/cashier/printers/${printer.id}/status`, { online, error: err })
        .catch(() => null);
      if (online) {
        toast.success(`${printer.station || printer.name} → online`);
      } else {
        toast.error(`${printer.station || printer.name} → ${err}`);
      }
      try {
        const { data: d } = await api.get('/cashier/printers/health');
        setData(d || { printers: [], online: 0, total: 0 });
      } catch { /* keep stale */ }
    } finally {
      setPingingIds((prev) => {
        const next = new Set(prev);
        next.delete(printer.id);
        return next;
      });
    }
  };

  // Probe + broadcast.
  const pingAll = async () => {
    setPinging(true);
    try {
      const alive = await isBridgeAlive();
      if (!alive) return;
      const results = await Promise.all(
        (data.printers || [])
          .filter((p) => !!p.ip_address)
          .map(async (p) => {
            try {
              const r = await pingPrinter(p.ip_address, p.port || 9100);
              return { id: p.id, online: !!r.ok, error: r.ok ? null : (r.data?.error || 'unreachable') };
            } catch (e) {
              return { id: p.id, online: false, error: String(e?.message || e) };
            }
          })
      );
      await Promise.all(
        results.map((r) =>
          api.post(`/cashier/printers/${r.id}/status`, { online: r.online, error: r.error }).catch(() => null)
        )
      );
      try {
        const { data: d } = await api.get('/cashier/printers/health');
        setData(d || { printers: [], online: 0, total: 0 });
      } catch { /* keep stale data */ }
    } finally {
      setPinging(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      if (cancelled) return;
      try {
        const { data: d } = await api.get('/cashier/printers/health');
        if (!cancelled) setData(d || { printers: [], online: 0, total: 0 });
      } catch {
        if (!cancelled) setData({ printers: [], online: 0, total: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    refresh();
    const t = setInterval(refresh, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // iter618 — Surface "Training mode" on the dashboard so HQ can see
  // when a branch is in training without having to walk over to the
  // POS. We piggy-back on the existing /branches data already loaded
  // by the dashboard. A separate small fetch keeps this widget
  // self-contained and avoids cross-widget coupling.
  const [trainingBranches, setTrainingBranches] = useState([]); // [{id, name, until}]
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get('/branches');
        if (cancelled || !Array.isArray(data)) return;
        const now = new Date();
        const active = data
          .filter((b) => b.training_mode_until && new Date(b.training_mode_until) > now)
          .map((b) => ({ id: b.id, name: b.name, until: b.training_mode_until }));
        setTrainingBranches(active);
      } catch { /* non-blocking */ }
    };
    tick();
    const i = setInterval(tick, 60 * 1000);
    return () => { cancelled = true; clearInterval(i); };
  }, []);

  // Hide the widget entirely if no printers are configured.
  if (!loading && (data.total === 0)) return null;

  // iter606 — Group printers by branch so multi-kitchen branches read
  // naturally ("Downtown · Cold Prep ✅ · Hot Grill ❌ · Bar ✅").
  // Within a branch we sort by role (receipt → kitchen → bar → label)
  // and then by station name so the same branch always renders in the
  // same order.
  const ROLE_ORDER = { receipt: 0, kitchen: 1, bar: 2, label: 3 };
  const groupedByBranch = (() => {
    const g = {};
    for (const p of (data.printers || [])) {
      const key = p.branch_name || 'All branches';
      (g[key] ||= []).push(p);
    }
    for (const key of Object.keys(g)) {
      g[key].sort((a, b) => {
        const r = (ROLE_ORDER[a.type] ?? 9) - (ROLE_ORDER[b.type] ?? 9);
        if (r !== 0) return r;
        return (a.station || a.name || '').localeCompare(b.station || b.name || '');
      });
    }
    return g;
  })();
  const branchKeys = Object.keys(groupedByBranch).sort();

  const offlineCount = (data.printers || []).filter((p) => p.online === false).length;
  const unknownCount = (data.printers || []).filter((p) => p.online === null).length;

  let bannerClass = 'bg-emerald-50 border-emerald-200';
  let titleColor = 'text-emerald-700';
  let TitleIcon = CheckCircle2;
  let summary = `${data.online} of ${data.total} online`;
  if (loading) {
    bannerClass = 'bg-slate-50 border-slate-200';
    titleColor = 'text-slate-500';
    TitleIcon = Loader2;
    summary = 'Checking…';
  } else if (offlineCount > 0) {
    bannerClass = 'bg-rose-50 border-rose-200';
    titleColor = 'text-rose-700';
    TitleIcon = AlertTriangle;
    summary = `${offlineCount} down · ${data.online}/${data.total} online`;
  } else if (unknownCount === data.total) {
    bannerClass = 'bg-amber-50 border-amber-200';
    titleColor = 'text-amber-700';
    TitleIcon = AlertTriangle;
    summary = 'Status not yet checked';
  }

  return (
    <Card className={`rounded-2xl border ${bannerClass} transition-colors`} data-testid="printer-health-widget">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-start gap-3 text-left min-w-0 flex-1"
            data-testid="printer-health-toggle"
          >
            <div className="w-9 h-9 bg-white/70 rounded-xl flex items-center justify-center shrink-0">
              <Printer size={17} className={titleColor} />
            </div>
            <div className="min-w-0">
              <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${titleColor}`}>
                <TitleIcon size={11} className={loading ? 'animate-spin' : ''} />
                Branch printers
              </div>
              <div className="text-lg font-bold font-outfit truncate" data-testid="printer-health-summary">
                {summary}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {branchKeys.length > 1 ? `${branchKeys.length} branches` : '1 branch'} · tap to {expanded ? 'collapse' : 'expand'}
              </div>
            </div>
          </button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8 rounded-lg shrink-0"
            onClick={pingAll}
            disabled={pinging || loading}
            title="Ping every printer now"
            data-testid="printer-health-refresh"
          >
            {pinging ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </Button>
        </div>

        {/* iter618 — Training-mode chips. Visible only when at least
            one branch is in training so HQ knows the prints there are
            currently going to OS dialog instead of paper. */}
        {trainingBranches.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="training-mode-dashboard-chips">
            <span className="text-[10px] uppercase font-bold tracking-wider text-amber-700">🎓 Training</span>
            {trainingBranches.map((b) => (
              <span
                key={b.id}
                title={`Training mode active until ${new Date(b.until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                className="text-[10px] font-semibold bg-amber-100 border border-amber-300 text-amber-900 px-2 py-0.5 rounded-full"
              >
                {b.name}
              </span>
            ))}
          </div>
        )}

        {expanded && (
          <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="printer-health-list">
            {branchKeys.length === 0 && (
              <div className="text-xs text-muted-foreground italic px-2">No printers configured.</div>
            )}
            {branchKeys.map((branchName) => (
              <BranchRow
                key={branchName}
                branchName={branchName}
                items={groupedByBranch[branchName]}
                onPing={pingOne}
                pingingIds={pingingIds}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
